import React, { useRef, useState } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import HeroRotatingCard from './HeroRotatingCard';
import { storePendingFile } from '../../utils/dbHelper';

export default function HeroSection({ onGetStarted }) {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);



  const [tab, setTab] = useState('upload');
  const [scratchName, setScratchName] = useState('');
  const [scratchRole, setScratchRole] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      await handleFileProcess(file);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      await handleFileProcess(file);
    }
  };

  const handleFileProcess = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) {
      alert("Only PDF and DOCX files are supported.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("File must be less than 5MB.");
      return;
    }
    setUploadingFile(file);
    try {
      await storePendingFile(file);
      sessionStorage.setItem('pending_import', 'true');
      if (onGetStarted) onGetStarted();
    } catch (err) {
      console.error(err);
      alert("Failed to queue file. Please try again.");
    }
  };

  const handleScratchSubmit = (e) => {
    e.preventDefault();
    if (!scratchName.trim() || !scratchRole.trim()) return;
    localStorage.setItem('pending_scratch_name', scratchName.trim());
    localStorage.setItem('pending_scratch_role', scratchRole.trim());
    if (onGetStarted) onGetStarted();
  };

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <section
      ref={sectionRef}
      className="min-h-screen flex items-center pt-8 pb-10 sm:pb-20 overflow-hidden transition-colors duration-300"
      style={{ background: 'var(--lp-bg)' }}
    >
      {/* Fixed 1fr left + 420px right — left always gets all remaining space */}
      {/* Expanded to 2 cols to give showcase more horizontal breathing room */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col lg:grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-20 items-center w-full">

        {/* ── Left Column (Top) ── wide text area */}
        <div className="flex flex-col items-start lg:col-start-1 lg:row-start-1 w-full">

          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-semibold tracking-widest uppercase select-none opacity-0"
            style={{
              animation: 'fadeUp 0.5s ease 0.1s forwards',
              border: '1px solid var(--lp-border)',
              color: 'var(--lp-text-muted)',
            }}
          >
            <span className="text-brand-gz-purple">✦</span> Free to use · No credit card
          </div>

          {/* H1 - Enhancv style matching local branding */}
          <h1
            className="font-display-gz font-bold leading-[1.08] tracking-tight mt-6 opacity-0"
            style={{
              fontSize: 'clamp(28px, 6vw, 56px)',
              animation: 'fadeUp 0.6s ease 0.2s forwards',
              color: 'var(--lp-text)',
            }}
          >
            Land more<br />interviews with<br />
            <span
              className="text-brand-gz-purple"
              style={{ textShadow: 'var(--lp-glow)' }}
            >
              Resumebar's<br />Resume Builder
            </span>
          </h1>

          {/* Subtext - Enhancv copy */}
          <p
            className="text-[17px] lg:text-[19px] leading-relaxed mt-5 opacity-0 max-w-xl"
            style={{ animation: 'fadeUp 0.5s ease 0.35s forwards', color: 'var(--lp-text-muted)' }}
          >
            ATS Check, AI Writer, and One-Click Job Tailoring make your resume stand out to recruiters.
          </p>

          {/* Interactive Start Card */}
          <div
            className="w-full mt-8 bg-white/60 dark:bg-[#14213D]/20 backdrop-blur-xl border border-zinc-200/80 dark:border-[#FCA311]/10 rounded-2xl p-5 shadow-xl transition-all duration-300 opacity-0"
            style={{
              animation: 'fadeUp 0.5s ease 0.45s forwards',
              color: 'var(--lp-text)',
            }}
          >
            {/* Tabs */}
            <div className="flex border-b border-zinc-200/80 dark:border-zinc-800 pb-3 mb-4 gap-6">
              <button
                type="button"
                onClick={() => setTab('upload')}
                className={`text-xs uppercase tracking-wider font-extrabold pb-1 transition-all relative cursor-pointer ${tab === 'upload' ? 'text-[#FCA311]' : 'text-zinc-450 dark:text-zinc-500 hover:text-zinc-650 dark:hover:text-zinc-300'}`}
              >
                Upload Resume (Fast Scan)
                {tab === 'upload' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FCA311] rounded-full" />}
              </button>
              <button
                type="button"
                onClick={() => setTab('scratch')}
                className={`text-xs uppercase tracking-wider font-extrabold pb-1 transition-all relative cursor-pointer ${tab === 'scratch' ? 'text-[#FCA311]' : 'text-zinc-450 dark:text-zinc-500 hover:text-zinc-650 dark:hover:text-zinc-300'}`}
              >
                Start Fresh (Free Build)
                {tab === 'scratch' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FCA311] rounded-full" />}
              </button>
            </div>

            {/* Tab: Upload */}
            {tab === 'upload' && (
              <div 
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-205 ${dragActive ? 'border-[#FCA311] bg-[#FCA311]/5' : 'border-zinc-300 dark:border-zinc-850 hover:border-[#FCA311]/50 bg-zinc-50/50 dark:bg-zinc-900/10'}`}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={onUploadClick}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  accept=".pdf,.docx" 
                  className="hidden" 
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-[#FCA311]/10 flex items-center justify-center text-[#FCA311] text-lg font-bold">
                    📎
                  </div>
                  <div className="text-sm font-bold text-zinc-700 dark:text-zinc-200">
                    {uploadingFile ? `Queuing ${uploadingFile.name}...` : 'Drag & drop your resume here'}
                  </div>
                  <div className="text-xs text-zinc-450 dark:text-zinc-500">
                    Supports PDF and DOCX (Max 5MB)
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Scratch */}
            {tab === 'scratch' && (
              <form onSubmit={handleScratchSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-zinc-450 dark:text-zinc-500 uppercase tracking-wider">Your Name</label>
                    <input 
                      type="text" 
                      required
                      placeholder="e.g. Alex Miller" 
                      value={scratchName}
                      onChange={e => setScratchName(e.target.value)}
                      className="bg-zinc-50/50 dark:bg-[#0B1220]/40 border border-zinc-250 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-800 dark:text-zinc-150 outline-none focus:border-[#FCA311] transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-zinc-450 dark:text-zinc-500 uppercase tracking-wider">Target Job Role</label>
                    <input 
                      type="text" 
                      required
                      placeholder="e.g. Software Engineer" 
                      value={scratchRole}
                      onChange={e => setScratchRole(e.target.value)}
                      className="bg-zinc-50/50 dark:bg-[#0B1220]/40 border border-zinc-250 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-800 dark:text-zinc-150 outline-none focus:border-[#FCA311] transition-all"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#14213D] hover:bg-[#0B1220] dark:bg-[#FCA311] dark:hover:bg-[#E59200] text-white dark:text-[#14213D] font-bold text-xs py-2.5 rounded-lg active:scale-98 transition-all cursor-pointer shadow-md tracking-wider uppercase"
                >
                  Start Building for Free →
                </button>
              </form>
            )}
          </div>

        </div>

        {/* ── Right Column ── */}
        <div className="flex items-start lg:items-center justify-center w-full lg:col-start-2 lg:row-span-2 h-[320px] sm:h-[560px] lg:h-auto mt-6 lg:mt-0 z-10">
          <div className="transform scale-[0.6] sm:scale-90 lg:scale-100 origin-top lg:origin-center w-full flex justify-center">
            <HeroRotatingCard />
          </div>
        </div>

        {/* ── Social proof / Trust stats ── */}
        <div
          className="flex flex-col lg:flex-row flex-wrap items-center lg:items-start justify-center lg:justify-start gap-y-6 lg:gap-y-4 gap-x-12 opacity-0 lg:col-start-1 lg:row-start-2 lg:mt-[-40px] pb-8 lg:pb-0"
          style={{ animation: 'fadeUp 0.5s ease 0.55s forwards' }}
        >
            {/* Reviews */}
            <div className="flex flex-col lg:flex-row items-center gap-2">
              <div className="flex items-center gap-[3px]">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-[#00b67a] p-[3px] rounded-sm">
                    <svg viewBox="0 0 24 24" fill="white" className="w-[14px] h-[14px]">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  </div>
                ))}
                <div className="bg-[#00b67a] p-[3px] rounded-sm relative">
                  <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.3)" className="w-[14px] h-[14px]">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                  <svg viewBox="0 0 24 24" fill="white" className="w-[14px] h-[14px] absolute top-[3px] left-[3px]" style={{ clipPath: 'inset(0 50% 0 0)' }}>
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                </div>
              </div>
              <span className="text-[14px] lg:text-[15px] mt-1 lg:mt-0 lg:ml-1" style={{ color: 'var(--lp-text)' }}>
                <span className="font-bold">5,279</span> Reviews
              </span>
            </div>

            {/* Hired users */}
            <div className="flex items-center gap-3 mt-4 lg:mt-0 text-center lg:text-left">
              <div className="flex-shrink-0" style={{ color: 'var(--lp-text-muted)' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 8.5C8 7.11929 9.11929 6 10.5 6H19.5C20.8807 6 22 7.11929 22 8.5V14.5C22 15.8807 20.8807 17 19.5 17H17V20L13.5 17H11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14.5 11H4.5C3.11929 11 2 12.1193 2 13.5V19.5C2 20.8807 3.11929 22 4.5 22H7V25L10.5 22H14.5C15.8807 22 17 20.8807 17 19.5V13.5C17 12.1193 15.8807 11 14.5 11Z" fill="var(--lp-bg)" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 15H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M6 18H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="text-[14px] lg:text-[15px] leading-snug" style={{ color: 'var(--lp-text)' }}>
                <span className="font-bold">28,452 users</span> landed interviews last month
              </span>
            </div>
          </div>
      </div>
    </section>
  );
}
